import { useId } from 'react';
import { SWOOSH_PATH } from './swoosh';

/*
  The JIT mark as a React component rather than an <img src>.

  Two reasons, both load-bearing:

  1. assets/brand/README.md is explicit that `currentColor` in jit-mark-mono.svg only
     inherits when the SVG is inline in the DOM. Through <img src> the file is an isolated
     document, currentColor resolves to black, and a white-on-dark header silently renders
     black-on-dark.
  2. The source SVGs theme themselves with a prefers-color-scheme media query, which
     cannot see our explicit data-theme attribute. Inlining lets the mark read the same
     --brand-* tokens as everything else, so the theme toggle moves it too.

  The mask around the monitor is not decoration. Without it the swoosh passes behind the
  monitor and fills the knocked-out screen aperture in the same ink, collapsing the
  one-colour variant into an unreadable blob. The mask ids are generated per instance with
  useId, because two copies of a hardcoded id on one page is a real rendering bug.
*/

export interface JitMarkProps {
  /** Rendered size in pixels. The mark is drawn on a 256 grid and scales cleanly. */
  readonly size?: number | undefined;
  readonly className?: string | undefined;
  /**
   * Decorative marks are hidden from assistive technology. Pass a label only when the
   * mark is the sole thing conveying "JIT" at that spot.
   */
  readonly label?: string;
}

export function JitMark({ size = 28, className, label }: JitMarkProps): React.JSX.Element {
  const uid = useId();
  const maskId = `jit-monitor-cut-${uid}`;
  const gradientId = `jit-swoosh-${uid}`;
  const titleId = `jit-mark-title-${uid}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      width={size}
      height={size}
      className={className}
      {...(label === undefined
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-labelledby': titleId })}
    >
      {label !== undefined ? <title id={titleId}>{label}</title> : null}
      <defs>
        <linearGradient
          id={gradientId}
          x1="30"
          y1="206"
          x2="214"
          y2="60"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--jit-blue-300)" />
          <stop offset="0.55" stopColor="var(--jit-blue-400)" />
          <stop offset="1" stopColor="var(--jit-blue-500)" />
        </linearGradient>
        <mask id={maskId}>
          <rect x="0" y="0" width="256" height="256" fill="#fff" />
          <g
            transform="rotate(-13 162 94)"
            fill="#000"
            stroke="#000"
            strokeWidth="14"
            strokeLinejoin="round"
          >
            <rect x="104" y="46" width="116" height="86" rx="13" />
            <rect x="153" y="132" width="18" height="13" />
            <rect x="132" y="143" width="60" height="12" rx="6" />
          </g>
        </mask>
      </defs>

      <g mask={`url(#${maskId})`}>
        <path d={SWOOSH_PATH} fill={`url(#${gradientId})`} />
      </g>

      <g transform="rotate(-13 162 94)">
        <rect x="104" y="46" width="116" height="86" rx="13" fill="var(--brand-bezel)" />
        <rect x="115" y="57" width="94" height="58" rx="5" fill="var(--brand-screen)" />
        <rect x="153" y="132" width="18" height="13" fill="var(--brand-bezel)" />
        <rect x="132" y="143" width="60" height="12" rx="6" fill="var(--brand-bezel)" />
      </g>
    </svg>
  );
}
